/**
 * The `confirmations` element's value, read (KPR-390; keepr-api
 * utils/confirmationValues.js is the API's pure half, utils/confirmations.js
 * the design).
 *
 *   { "name": "me-too", "label": { "singular": "Me too" }, "dataType": "confirmations" }
 *
 * The stored value `item.elements[name]` is HOW MANY people said "me too": a
 * whole number, ABSENT until the first confirmation, and absent reads as 0
 * everywhere — the read view, a tile, a cell, a sort, a filter, a chart.
 * Who confirmed is never on the item (only the managers' people door reads
 * it), and nobody types the count: the only writes are a person's own
 * confirm and take-back (services/confirmations.js). So every create path
 * leaves it out, an edit never sends it, a copy never carries it, and no
 * form, rule, formula, template or import offers it.
 *
 * Its words are its LABEL ("Me too", "I see this too", "+1") — the button
 * says what the element is called.
 */

import { apiErrorMessage, formatWait, retryAfterSeconds } from './apiErrors';

export const CONFIRMATIONS_TYPE = 'confirmations';

export const isConfirmationsType = dataType => dataType === CONFIRMATIONS_TYPE;
export const isConfirmationsElement = element => !!element && element.dataType === CONFIRMATIONS_TYPE;

/**
 * A stored count as the number it means: absent, blank, or anything that is
 * not a whole number ≥ 0 reads as 0 (the API's readCount, rule for rule).
 */
export const readCount = value => (typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0);

/** The element's words — its label, else its name. On the button, in the tooltip. */
export const confirmationsLabel = (element) => {
	const label = element?.label;
	const text = label && typeof label === 'object' ? label.singular : label;
	const trimmed = typeof text === 'string' ? text.trim() : '';
	return trimmed || String(element?.name || '');
};

/**
 * `elements` without a key for any confirmations element in `defs` — what a
 * create or an edit puts on the wire. Never mutates.
 *
 * Left OUT, not echoed: the item form reads the count once, and somebody can
 * say "me too" while it is open without moving `updatedAt` (a confirmation is
 * not a content change), so an echo could be a stale count. Under the form's
 * default merge an omitted key keeps what is stored; the API puts the stored
 * count back whatever it is sent, and refuses a different one under an
 * explicit `merge: true` (400 element_read_only).
 */
export function withoutConfirmations(elements, defs) {
	if (!elements || typeof elements !== 'object') return elements;
	const names = (Array.isArray(defs) ? defs : []).filter(isConfirmationsElement).map(def => def.name).filter(Boolean);
	if (!names.some(name => Object.prototype.hasOwnProperty.call(elements, name))) return elements;
	const next = { ...elements };
	names.forEach(name => { delete next[name]; });
	return next;
}

/**
 * The sentence the item form and a cell's "not editable" reason say: nobody
 * types the count.
 */
export const countedFromSentence = element => `Counted from the people who say “${confirmationsLabel(element) || 'Me too'}”. Nobody types it.`;

/** A count with its noun, for a reader: "1 person", "12 people"; null at zero. */
export const peopleCount = (count) => {
	const n = readCount(count);
	if (!n) return null;
	return `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;
};

/**
 * A confirm or take-back refusal as the sentence a person reads (keepr-api
 * SCHEMA.md § Confirmations, "Who may confirm"). The API's own sentence where
 * it has one — `email_unverified`'s through apiErrors (it names the profile's
 * resend), the archived sentence, `item_pending`, `confirm_not_allowed` —
 * and the budget's words for a 429: the ten changes an hour per item
 * (`budget: 'toggle'`) say so, the person's own minute and day budgets say
 * "a lot in a short time", each with when to try again.
 */
export function confirmationRefusal(error, { label = 'Me too' } = {}) {
	const words = label || 'Me too';
	if (!error?.response) return apiErrorMessage(error);
	const { status } = error.response;
	const data = error.response.data || {};
	if (status === 429) {
		const wait = retryAfterSeconds(error);
		const when = wait ? `Try again in ${formatWait(wait)}.` : 'Try again in a little while.';
		if (data.budget === 'toggle') {
			return `You have changed your “${words}” on this item ${Number(data.limit) || 10} times this hour, the most it can be. ${when}`;
		}
		return `That is a lot of confirmations in a short time. ${when}`;
	}
	if (status === 404 && data.code === 'unknown_element') return `This item has no “${words}” any more. Reload to see it as it is now.`;
	if (status === 404) return 'This item is not there any more, or you can no longer open it.';
	return apiErrorMessage(error, `Your “${words}” could not be saved. Please try again.`);
}
